import React, { useEffect, useRef, useState } from 'react';
import { reorderCategories } from './categoryOrdering';


const EmployeeCategoryManager = ({
  categories,
  saving,
  onReorder,
  onColorChange,
  onRename,
  onDelete,
}) => {
  const [displayedCategories, setDisplayedCategories] = useState(categories);
  const [draggedCategoryId, setDraggedCategoryId] = useState(null);
  const displayedRef = useRef(categories);
  const gestureRef = useRef(null);

  useEffect(() => {
    if (!gestureRef.current) {
      displayedRef.current = categories;
      setDisplayedCategories(categories);
    }
  }, [categories]);

  const clearGesture = () => {
    gestureRef.current = null;
    setDraggedCategoryId(null);
  };

  const handlePointerDown = (event, categoryId) => {
    if (saving) return;
    event.preventDefault();
    gestureRef.current = {
      pointerId: event.pointerId,
      draggedId: categoryId,
    };
    setDraggedCategoryId(categoryId);
  };

  const handlePointerMove = (event) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    event.preventDefault();

    const targetRow = document.elementFromPoint(event.clientX, event.clientY)
      ?.closest('[data-category-id]');
    const targetId = targetRow?.dataset.categoryId;
    if (!targetId || targetId === gesture.draggedId) return;

    const current = displayedRef.current;
    const fromIndex = current.findIndex(category => category.id === gesture.draggedId);
    const toIndex = current.findIndex(category => category.id === targetId);
    const reordered = reorderCategories(current, fromIndex, toIndex);
    if (reordered === current) return;

    displayedRef.current = reordered;
    setDisplayedCategories(reordered);
  };

  const handlePointerUp = (event) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    clearGesture();
  };

  const handlePointerCancel = (event) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    clearGesture();
  };

  const hasPendingOrder =
    categories.length !== displayedCategories.length
    || categories.some(
      (category, index) => category.id !== displayedCategories[index]?.id
    );

  const saveDisplayedOrder = () => {
    if (!hasPendingOrder || saving) return;
    onReorder(categories, displayedRef.current);
  };

  useEffect(() => {
    if (!draggedCategoryId) return undefined;

    window.addEventListener('pointermove', handlePointerMove, { passive: false });
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, [draggedCategoryId]);

  return (
    <div className="space-y-2 mb-3">
      {displayedCategories.map(category => (
        <div
          key={category.id}
          className={`flex flex-wrap items-center gap-2 rounded border bg-white p-2 transition-colors ${
            draggedCategoryId === category.id ? 'border-blue-400 bg-blue-50 shadow-sm' : 'border-gray-200'
          }`}
          data-category-id={category.id}
          data-testid={`category-row-${category.id}`}
        >
          <button
            type="button"
            disabled={saving}
            onPointerDown={(event) => handlePointerDown(event, category.id)}
            aria-label="Kategorie verschieben"
            aria-grabbed={draggedCategoryId === category.id}
            title="Zum Sortieren ziehen"
            className="cursor-grab rounded px-1.5 py-1 text-lg leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-wait disabled:opacity-40 active:cursor-grabbing"
            style={{ touchAction: 'none' }}
            data-testid={`category-drag-${category.id}`}
          >
            ≡
          </button>

          <input
            type="color"
            value={category.color || '#6B7280'}
            onChange={(event) => onColorChange(category.id, event.target.value)}
            className="h-8 w-8 cursor-pointer rounded border border-gray-300"
            title="Farbe ändern"
            data-testid={`category-color-${category.id}`}
          />
          <span className="min-w-24 flex-1 truncate font-medium text-gray-800">
            {category.name}
          </span>
          <button
            type="button"
            onClick={() => onRename(category.id, category.name)}
            className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid={`category-rename-${category.id}`}
          >
            Umbenennen
          </button>
          <button
            type="button"
            onClick={() => onDelete(category.id, category.name)}
            className="px-2 py-1 text-xs text-red-600 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500"
            data-testid={`category-delete-${category.id}`}
          >
            Löschen
          </button>
        </div>
      ))}

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={saveDisplayedOrder}
          disabled={!hasPendingOrder || saving}
          data-testid="category-order-save"
          className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
        >
          {saving ? 'Wird gespeichert …' : 'Reihenfolge speichern'}
        </button>
      </div>
    </div>
  );
};


export default EmployeeCategoryManager;
